import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, CheckCircle, Lightbulb, Clock, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import Navbar from "@/components/navbar";
import { type Idea } from "@shared/schema";

const categoryIcons = {
  'Feature': Star,
  'Enhancement': Plus,
  'Performance': Clock,
  'UI/UX': Users,
  'Analytics': Lightbulb,
  'Bug Fix': CheckCircle,
  'Security': CheckCircle,
  'Integration': Plus,
  'Mobile': Users,
  'Database': Clock,
};

const priorityColors = {
  'Low': 'bg-green-100 text-green-800 border-green-300',
  'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'High': 'bg-orange-100 text-orange-800 border-orange-300',
  'Critical': 'bg-red-100 text-red-800 border-red-300',
};

const statusColors = {
  'Suggested': 'bg-blue-100 text-blue-800 border-blue-300',
  'In Progress': 'bg-purple-100 text-purple-800 border-purple-300',
  'Completed': 'bg-green-100 text-green-800 border-green-300',
};

function AddIdeaForm({ onClose }: { onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Feature',
    priority: 'Medium'
  });

  const addIdeaMutation = useMutation({
    mutationFn: (data: any) => apiRequest('/api/admin/ideas', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/ideas'] });
      toast({
        title: "Success",
        description: "New idea added successfully"
      });
      onClose();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    addIdeaMutation.mutate({
      ...formData,
      status: 'Suggested',
      dateAdded: new Date().toISOString().split('T')[0]
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Brief description of the idea"
        />
      </div>
      
      <div>
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Detailed explanation of the idea and its benefits"
          rows={3}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Feature">Feature</SelectItem>
              <SelectItem value="Enhancement">Enhancement</SelectItem>
              <SelectItem value="Performance">Performance</SelectItem>
              <SelectItem value="UI/UX">UI/UX</SelectItem>
              <SelectItem value="Analytics">Analytics</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="priority">Priority</Label>
          <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={addIdeaMutation.isPending}>
          Add Idea
        </Button>
      </div>
    </form>
  );
}

export default function AdminIdeas() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('Suggested');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  const { data: ideas = [] } = useQuery<Idea[]>({
    queryKey: ['/api/admin/ideas'],
  });

  const deleteIdeaMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/admin/ideas/${id}`, {
      method: 'DELETE'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/ideas'] });
      toast({
        title: "Success",
        description: "Idea deleted successfully"
      });
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number, status: string }) => 
      apiRequest(`/api/admin/ideas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/ideas'] });
      toast({
        title: "Success", 
        description: "Idea status updated successfully"
      });
    }
  });

  const updatePriorityMutation = useMutation({
    mutationFn: ({ id, priority }: { id: number, priority: string }) => 
      apiRequest(`/api/admin/ideas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ priority })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/ideas'] });
      toast({
        title: "Success", 
        description: "Idea priority updated successfully"
      });
    }
  });

  const filteredIdeas = ideas.filter(idea => {
    const statusMatch = filterStatus === 'all' || idea.status === filterStatus;
    const categoryMatch = filterCategory === 'all' || idea.category === filterCategory;
    const priorityMatch = filterPriority === 'all' || 
      (filterPriority === 'high' && (idea.priority === 'High' || idea.priority === 'Critical')) ||
      (filterPriority !== 'high' && idea.priority === filterPriority);
    return statusMatch && categoryMatch && priorityMatch;
  });

  const handleDelete = (id: number) => {
    deleteIdeaMutation.mutate(id);
  };

  const handleStatusChange = (id: number, status: string) => {
    updateStatusMutation.mutate({ id, status });
  };

  const handlePriorityChange = (id: number, priority: string) => {
    updatePriorityMutation.mutate({ id, priority });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8 pt-24">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-archivo font-bold text-slate-900 dark:text-white mb-2">
              Ideas Log Sheet
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Track feature suggestions, enhancements, and improvements for the platform
            </p>
          </div>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-max-brand hover:bg-max-brand/90">
                <Plus className="h-4 w-4 mr-2" />
                Add New Idea
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Idea</DialogTitle>
                <DialogDescription>
                  Add a new feature suggestion or enhancement idea to the log
                </DialogDescription>
              </DialogHeader>
              <AddIdeaForm onClose={() => setIsAddDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <Label htmlFor="status-filter">Filter by Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Suggested">Suggested</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            <Label htmlFor="category-filter">Filter by Category</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Feature">Feature</SelectItem>
                <SelectItem value="Enhancement">Enhancement</SelectItem>
                <SelectItem value="Performance">Performance</SelectItem>
                <SelectItem value="UI/UX">UI/UX</SelectItem>
                <SelectItem value="Analytics">Analytics</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards - Quick Filter Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => {
              setFilterStatus('all');
              setFilterCategory('all');
              setFilterPriority('all');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Lightbulb className="h-4 w-4 text-blue-500" />
                <div className="ml-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Total Ideas</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{ideas.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => {
              setFilterStatus('In Progress');
              setFilterCategory('all');
              setFilterPriority('all');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-purple-500" />
                <div className="ml-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">In Progress</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {ideas.filter((i: Idea) => i.status === 'In Progress').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => {
              setFilterStatus('Completed');
              setFilterCategory('all');
              setFilterPriority('all');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <div className="ml-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Completed</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {ideas.filter((i: Idea) => i.status === 'Completed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => {
              setFilterStatus('all');
              setFilterCategory('all');
              setFilterPriority('high');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Star className="h-4 w-4 text-orange-500" />
                <div className="ml-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">High Priority</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {ideas.filter((i: Idea) => i.priority === 'High' || i.priority === 'Critical').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ideas Grid */}
        <div className="grid gap-6">
          {filteredIdeas.map((idea: Idea) => {
            const CategoryIcon = categoryIcons[idea.category as keyof typeof categoryIcons] || Lightbulb;
            return (
              <Card key={idea.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <CategoryIcon className="h-5 w-5 text-max-brand mt-1" />
                      <div>
                        <CardTitle className="text-lg">{idea.title}</CardTitle>
                        <CardDescription className="mt-2">{idea.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={idea.status}
                        onValueChange={(value) => handleStatusChange(idea.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Suggested">Suggested</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(idea.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className={priorityColors[idea.priority as keyof typeof priorityColors]}>
                        {idea.priority} Priority
                      </Badge>
                      <Badge variant="outline" className={statusColors[idea.status as keyof typeof statusColors]}>
                        {idea.status}
                      </Badge>
                      <span className="text-sm text-slate-500">{idea.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={idea.priority}
                        onValueChange={(value) => handlePriorityChange(idea.id, value)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-slate-400">{idea.dateAdded}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}